import { useEffect, useRef, useState } from "react";
import CrashPage from "../../ErrorPages/CrashPage";
import { useIsMobile } from "../../hooks/useDisplayMobileWall";
import type { RemoteName } from "../../mfe/mfeConfig";
import { loadMFE } from "../../mfe/mfeLoaders";
import { useCurrentMfe } from "./state";
import type { MfeRouteConfig } from "./types";

type RouterProps = {
  config: Record<RemoteName, MfeRouteConfig>;
};

export const Router = ({ config }: RouterProps) => {
  const isMobile = useIsMobile();
  const currentMfe = useCurrentMfe(config);

  const [error, setError] = useState<null | unknown>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const unmountFn = (async (): Promise<() => void> => {
      if (currentMfe) {
        setError(null);
        try {
          const element = elementRef.current;

          const renderMfe = await (currentMfe?.import
            ? currentMfe.import()
            : loadMFE(currentMfe.name, currentMfe.entry));

          if (element != null && currentMfe) {
            const newElement = document.createElement("div");

            element.appendChild(newElement);
            const unmountMfe = renderMfe(newElement, {
              basePath: currentMfe.path.replace(/\/\*$/, ""),
            });
            return () => {
              if (typeof unmountMfe === "function") {
                unmountMfe();
              } else {
                console.warn(
                  "MFE should return an unmount function! Got instead:",
                  typeof unmountFn
                );
              }
              element.removeChild(newElement);
            };
          }
        } catch (error) {
          setError(error);
        }
      }
      return () => null;
    })();

    return () => {
      unmountFn.then((u) => u());
    };
  }, [currentMfe]);

  return (
    <>
      {error && <CrashPage error={error} />}
      <div
        style={{ height: "100%", width: isMobile ? "100vw" : "100%" }}
        ref={elementRef}
      />
    </>
  );
};
